import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../contexts/user.context';
import { AddEmailModel, EmailResetModel, PasswordUpdateModel, SignupEmailModel, UserProviderValue, UserPutModel } from '../models';
import { useForm } from 'react-hook-form';
import { AddEmailSchema, EmailResetSchema, PasswordUpdateSchema, UserPutSchema } from '../schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import {
   addEmailCheck,
   deleteWallet,
   linkAppleAccount,
   linkGoogleAccount,
   putUser,
   putUserPassword,
   requestEmailVerification,
   setPrimaryWallet,
   signupEmail,
   unlinkAppleAccount,
   unlinkGoogleAccount,
   uploadAvatar,
} from '../services';
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import { SignupStatuses } from '../enums';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAppleAuth } from './useAppleAuth';
import { useGoogleAuth } from './useGoogleAuth';

export const useAccount = () => {
   const { user, isAuthenticated, setUser, setIsAuthenticated, fetchUser } = useContext<UserProviderValue>(UserContext);
   const wallet = useWallet();
   const userPutForm = useForm<UserPutModel>({
      resolver: zodResolver(UserPutSchema),
      mode: 'onChange',
   });

   const userPasswordPutForm = useForm<PasswordUpdateModel>({
      resolver: zodResolver(PasswordUpdateSchema),
      mode: 'onChange',
   });

   const addEmailForm = useForm<AddEmailModel>({
      resolver: zodResolver(AddEmailSchema),
      mode: 'onChange',
   });

   const resetEmailForm = useForm<EmailResetModel>({
      resolver: zodResolver(EmailResetSchema),
      mode: 'onChange',
      defaultValues: {
         email: user?.email || '',
      },
   });

   const fileInputRef = useRef<any>(null);
   const [isUserPasswordLoading, setIsUserPasswordLoading] = useState(false);
   const [isUserLoading, setIsUserLoading] = useState(false);
   const [isAvatarLoading, setIsAvatarLoading] = useState(false);
   const [allowAccountUpdate, setAllowAccountUpdate] = useState(false);
   const [addEmailStatus, setAddEmailStatus] = useState<SignupStatuses>(SignupStatuses.Initial);
   const [isAddEmailLoading, setIsAddEmailLoading] = useState(false);
   const [addEmailData, setAddEmailData] = useState<AddEmailModel | null>(null);

   const [isResetEmailLoading, setIsResetEmailLoading] = useState(false);
   const [resetEmailStatus, setResetEmailStatus] = useState<SignupStatuses>(SignupStatuses.Initial);
   // const [resetEmailData, setResetEmailData] = useState<EmailResetModel | null>(null);

   const [isUpdatingPrimary, setIsUpdatingPrimary] = useState(false);
   const [isDeletingWallet, setIsDeletingWallet] = useState(false);

   const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
   const [isLinkingApple, setIsLinkingApple] = useState(false);
   const [isUnlinkingGoogle, setIsUnlinkingGoogle] = useState(false);
   const [isUnlinkingApple, setIsUnlinkingApple] = useState(false);

   useEffect(() => {
      if (user) {
         userPutForm.reset({
            username: user.username,
            fullname: user.fullname,
            address: user.address,
            city: user.city,
            state: user.state,
            zip: user.zip,
            country: user.country,
            phone: user.phone,
         });
      }
   }, [user]);

   const handleImageDivClick = () => {
      if (fileInputRef.current) {
         fileInputRef.current.click();
      }
   };

   const handleFileChange = async (event: any) => {
      const selectedFile = event.target.files[0];
      setIsAvatarLoading(true);
      if (selectedFile?.type.startsWith('image/')) {
         try {
            const options = {
               maxSizeMB: 0.2, // (200kb)
               maxWidthOrHeight: 1920,
               useWebWorker: true,
            };

            const compressedFile = await imageCompression(selectedFile, options);

            const formData = new FormData();
            formData.append('file', compressedFile);

            const response = await uploadAvatar(formData);

            if (response.error || !response.data) {
               toast.error(response.error?.message || 'Failed to upload image');
               setIsAvatarLoading(false);
               return;
            }

            setUser(user ? { ...response.data, primaryWallet: user.primaryWallet, wallets: user.wallets } : response.data);
            toast.success('Image uploaded successfully');
         } catch (error: any) {
            if (error.message) {
               toast.error(error.message);
            } else {
               toast.error('An unexpected error occurred while compressing the image.');
            }
         }
      }
      setIsAvatarLoading(false);
   };

   const submitEditAccount = async (data: UserPutModel) => {
      setIsUserLoading(true);
      // Add logic to handle account update
      const editUserResponse = await putUser(data);
      if (editUserResponse.error || !editUserResponse.data) {
         toast.error(editUserResponse.error?.message || 'Failed to save changes');
         setIsUserLoading(false);
         return;
      }
      setUser(user ? { ...editUserResponse.data, primaryWallet: user.primaryWallet, wallets: user.wallets } : editUserResponse.data);

      userPutForm.reset();

      toast.success('Changes saved successfully');
      setIsUserLoading(false);
      setAllowAccountUpdate(false);
   };

   const submitEditPassword = async (data: PasswordUpdateModel) => {
      setIsUserPasswordLoading(true);
      // Add logic to handle password update
      const editPasswordResponse = await putUserPassword(data);
      if (editPasswordResponse.error || !editPasswordResponse.data) {
         toast.error(editPasswordResponse.error?.message || 'Failed to save changes');
         setIsUserPasswordLoading(false);
         return;
      }

      toast.success('Password updated successfully');
      setUser(user ? { ...editPasswordResponse.data, primaryWallet: user.primaryWallet, wallets: user.wallets } : editPasswordResponse.data);

      userPasswordPutForm.reset();

      setIsUserPasswordLoading(false);
   };

   const submitCheckAddEmail = async (data: SignupEmailModel) => {
      console.log('data', data);
      console.log('addEmailForm.formState.isValid', addEmailForm.formState.isValid);
      if (!addEmailForm.formState.isValid) return;
      setIsAddEmailLoading(true);

      setAddEmailData(data);

      // check info first
      const checked = await addEmailCheck(data);
      if (checked.error) {
         toast.error(checked.error.message || 'Failed to check email');
         setIsAddEmailLoading(false);
         setAddEmailStatus(SignupStatuses.Initial);
         return;
      }

      setAddEmailStatus(SignupStatuses.InformationVerified);

      const requestEmailVerificationResponse = await requestEmailVerification(data.email);
      if (requestEmailVerificationResponse.error) {
         toast.error(requestEmailVerificationResponse.error.message || 'Failed to send verification email');
         setIsAddEmailLoading(false);

         return;
      }
      setAddEmailStatus(SignupStatuses.VerificationEmailSent);
      setIsAddEmailLoading(false);
   };

   const submitAddEmail = async (validationCode: string) => {
      if (!addEmailData) {
         toast.error('An error occurred. Please try again');
         setAddEmailStatus(SignupStatuses.Initial);
         return;
      }
      if (!validationCode) {
         toast.error('Please enter the validation code');
         return;
      }

      setIsAddEmailLoading(true);
      const addEmailResponse = await signupEmail({ ...addEmailData, validationCode });
      if (addEmailResponse.error || !addEmailResponse.data) {
         toast.error(addEmailResponse.error?.message || 'Failed to Add your email');
         setIsAddEmailLoading(false);
         return;
      }

      setUser(user ? { ...addEmailResponse.data, primaryWallet: user.primaryWallet, wallets: user.wallets } : addEmailResponse.data);
      resetEmailForm.reset({
         email: addEmailResponse.data?.email || '',
      });
      toast.success('Email successfully linked');
      setIsAddEmailLoading(false);
      setIsAuthenticated(true);
   };

   const submitResetCheckEmail = async (data: EmailResetModel) => {
      // if (!resetEmailForm.formState.isValid) return;
      // setIsResetEmailLoading(true);
      // setResetEmailData(data);

      // const checked = await resetEmailCheck(data);
      // if (checked.error) {
      //    toast.error(checked.error.message || 'Failed to check email');
      //    setIsResetEmailLoading(false);
      //    setResetEmailStatus(SignupStatuses.Initial);
      //    resetEmailForm.reset({
      //       email: user?.email || '',
      //    });
      //    return;
      // }

      // setResetEmailStatus(SignupStatuses.InformationVerified);
      // const requestEmailVerificationResponse = await requestEmailVerification(data.email);
      // if (requestEmailVerificationResponse.error) {
      //    toast.error(requestEmailVerificationResponse.error.message || 'Failed to send verification email');
      //    setIsResetEmailLoading(false);
      //    return;
      // }
      // setResetEmailStatus(SignupStatuses.VerificationEmailSent);
      // setIsResetEmailLoading(false);

      return;
   };

   const submitResetEmail = async (validationCode: string) => {
      // if (!resetEmailData) {
      //    toast.error('An error occurred. Please try again');
      //    setResetEmailStatus(SignupStatuses.Initial);
      //    return;
      // }
      // if (!validationCode) {
      //    toast.error('Please enter the validation code');
      //    return;
      // }

      // setIsResetEmailLoading(true);
      // const resetEmailResponse = await resetEmail({ ...resetEmailData, validationCode });
      // if (resetEmailResponse.error || !resetEmailResponse.data) {
      //    toast.error(resetEmailResponse.error?.message || 'Failed to Add your email');
      //    setIsResetEmailLoading(false);
      //    return;
      // }

      // setUser(resetEmailResponse.data);

      // setResetEmailStatus(SignupStatuses.Initial);

      // toast.success('Email successfully linked');
      // setIsResetEmailLoading(false);
      // setIsAuthenticated(true);

      return;
   };

   const handleSetPrimaryWallet = async (publicKey: string) => {
      setIsUpdatingPrimary(true);
      const response = await setPrimaryWallet(publicKey);
      if (response.error) {
         toast.error('Failed to update primary wallet');
         setIsUpdatingPrimary(false);

         return;
      }
      toast.success('Primary wallet updated successfully');
      setUser(response.data);
      setIsUpdatingPrimary(false);
   };

   const handleDeleteWallet = async (publicKey: string) => {
      setIsDeletingWallet(true);
      const response = await deleteWallet(publicKey);
      if (response.error) {
         toast.error(response.error.message || 'Failed to delete wallet');
         setIsDeletingWallet(false);
         return;
      }
      toast.success('Wallet deleted successfully');
      setUser(response.data);
      if (wallet?.publicKey?.toString() === publicKey) {
         wallet?.disconnect();
      }
      setIsDeletingWallet(false);
   };

   const handleGoogleLinkSuccess = async (idToken: string) => {
      if (!user) return;
      setIsLinkingGoogle(true);
      const response = await linkGoogleAccount({ idToken });
      if (response.error) {
         toast.error(response.error.message || 'Failed to link Google account');
         setIsLinkingGoogle(false);
         return;
      }
      toast.success('Google account linked successfully');
      setIsLinkingGoogle(false);
      await fetchUser();
   };

   const { signInWithGoogle: connectGoogle, isGoogleReady: isGoogleConnectReady } = useGoogleAuth({
      onSuccess: handleGoogleLinkSuccess,
   });

   const handleGoogleUnlink = async () => {
      if (!user) return;
      setIsUnlinkingGoogle(true);
      const response = await unlinkGoogleAccount();
      if (response.error) {
         toast.error(response.error.message || 'Failed to unlink Google account');
         setIsUnlinkingGoogle(false);
         return;
      }
      toast.success('Google account unlinked successfully');
      setIsUnlinkingGoogle(false);
      await fetchUser();
   };

   const handleAppleLinkSuccess = async (idToken: string) => {
      if (!user) return;
      setIsLinkingApple(true);
      const response = await linkAppleAccount({ idToken });
      if (response.error) {
         toast.error(response.error.message || 'Failed to link Apple account');
         setIsLinkingApple(false);
         return;
      }
      toast.success('Apple account linked successfully');
      setIsLinkingApple(false);
      await fetchUser();
   };

   const { signInWithApple: connectApple, isAppleReady: isAppleConnectReady } = useAppleAuth({
      onSuccess: handleAppleLinkSuccess,
   });

   const handleAppleUnlink = async () => {
      if (!user) return;
      setIsUnlinkingApple(true);
      const response = await unlinkAppleAccount();
      if (response.error) {
         toast.error(response.error.message || 'Failed to unlink Apple account');
         setIsUnlinkingApple(false);
         return;
      }
      toast.success('Apple account unlinked successfully');
      setIsUnlinkingApple(false);
      await fetchUser();
   };

   return {
      user,
      isAuthenticated,
      setUser,
      setIsAuthenticated,
      submitEditAccount,
      submitEditPassword,
      userPutForm,
      userPasswordPutForm,
      isUserPasswordLoading,
      isUserLoading,
      allowAccountUpdate,
      setAllowAccountUpdate,
      fileInputRef,
      isAvatarLoading,
      handleImageDivClick,
      handleFileChange,
      addEmailForm,

      submitCheckAddEmail,
      submitAddEmail,
      addEmailStatus,
      setAddEmailStatus,
      isAddEmailLoading,
      resetEmailForm,

      submitResetCheckEmail,
      submitResetEmail,
      resetEmailStatus,
      setResetEmailStatus,
      isResetEmailLoading,
      handleSetPrimaryWallet,
      isUpdatingPrimary,
      isDeletingWallet,
      handleDeleteWallet,

      connectGoogle,
      isGoogleConnectReady,
      connectApple,
      isAppleConnectReady,
      isLinkingGoogle,
      isLinkingApple,
      isUnlinkingGoogle,
      isUnlinkingApple,
      handleGoogleUnlink,
      handleAppleUnlink,
   };
};
